import React, { useEffect, useMemo, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "./mapa.css";
import { MapContainer, TileLayer, Marker, Polygon, useMap, useMapEvents } from "react-leaflet";
import pin_icone from "../../../assets/icons/pin.svg";
import pin_plus_icone from "../../../assets/pin-plus.png";
import pin_pro_icone from "../../../assets/pin-pro.png";
import pin_success_icone from "../../../assets/pin-success.png";
import pin_cluster_icone from "../../../assets/pin_cluster.png";

// O leaflet.markercluster e um plugin UMD que procura o `L` GLOBAL; com bundler o
// Leaflet nao o expoe sozinho, e o plugin quebra ao carregar ("reading 'markers'")
// -- derrubando o app inteiro, ate a tela de login. Por isso o global e definido
// ANTES do require (um `import` seria elevado para antes desta linha).
window.L = L;
require("leaflet.markercluster");

// Mapa baseado em Leaflet + OpenStreetMap. Substitui o Google Maps, que exigia
// chave e faturamento (em producao falhava com BillingNotEnabledMapError).
//
// A API de props e a mesma do componente antigo, para as telas Plantar e Mapa
// nao mudarem. Onde o Google entregava um objeto `latLng` com metodos lat() e
// lng(), continuamos entregando o mesmo formato (ver `comoLatLng`).

const CENTRO_PADRAO = { lat: -20.3197, lng: -40.3385 };
const TILES = "https://tile.openstreetmap.org/{z}/{x}/{y}.png";
// A licenca do OpenStreetMap exige a atribuicao visivel.
const ATRIBUICAO = '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap</a> contributors';

// Objeto simples, nao `new Map()`: o componente deste arquivo se chamava `Map`
// e sombreava o Map global, entao `new Map()` construia o COMPONENTE e derrubava
// o app inteiro na carga. O componente agora se chama `Mapa`.
const icones = {};
function icone(url) {
  if (!icones[url]) {
    // L.icon com as imagens do projeto: o icone padrao do Leaflet quebra com
    // bundlers (procura o PNG por um caminho que o webpack nao gera).
    icones[url] = L.icon({ iconUrl: url, iconSize: [32, 32], iconAnchor: [16, 16] });
  }
  return icones[url];
}

// Ícone das PONTAS da área Profissional: mesma imagem, mas com a classe que dá o
// cursor de "mão" e o destaque ao passar o mouse (mapa.css).
const iconesVertice = {};
// "+" no MEIO de cada lado do poligono fechado: clicar acrescenta uma ponta ali (e a
// pessoa a arrasta para onde quiser). E o gesto de editor de poligono mais comum, e
// os marcadores do Leaflet tambem sao focaveis pelo teclado (Enter dispara o clique).
const ICONE_MEIO = L.divIcon({
  className: "",
  iconSize: [20, 20],
  iconAnchor: [10, 10],
  html: '<div style="width:20px;height:20px;border-radius:50%;background:#fff;border:2px solid #3E8E5B;color:#3E8E5B;font:700 16px/16px sans-serif;text-align:center;box-shadow:0 1px 4px rgba(0,0,0,.35);cursor:copy;">+</div>',
});

function iconeVertice(url) {
  if (!iconesVertice[url]) {
    iconesVertice[url] = L.icon({ iconUrl: url, iconSize: [32, 32], iconAnchor: [16, 16], className: "vertice-editavel" });
  }
  return iconesVertice[url];
}

// Mesmo formato que o Google entregava: { latLng: { lat(), lng() } }.
const comoLatLng = (lat, lng) => ({ lat: () => lat, lng: () => lng });

const valido = (p) => p && Number.isFinite(p.lat) && Number.isFinite(p.lng);
const comoPonto = (p) => (p ? { lat: parseFloat(p.lat), lng: parseFloat(p.lng), zoom: p.zoom } : null);

// Leva o mapa ate `alvo` quando ele muda. `zoom` e opcional: sem ele mantem o atual.
function Recentralizar({ alvo }) {
  const map = useMap();
  const chave = valido(alvo) ? `${alvo.lat},${alvo.lng},${alvo.zoom ?? ""}` : "";
  useEffect(() => {
    if (!chave) return;
    map.setView([alvo.lat, alvo.lng], alvo.zoom ?? map.getZoom(), { animate: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chave, map]);
  return null;
}

// Na edicao de um plantio, enquadra o que ja foi registrado (uma vez).
// Area ja registrada, so para leitura (tela Mapa): enquadra o poligono inteiro.
function EnquadrarArea({ area }) {
  const map = useMap();
  useEffect(() => {
    if (area?.length >= 3) map.fitBounds(area, { padding: [50, 50], maxZoom: 17 });
  }, [area, map]);
  return null;
}

function EnquadrarPlantio({ pontos, ativo }) {
  const map = useMap();
  const feito = useRef(false);
  useEffect(() => {
    if (!ativo) return;
    if (pontos.length === 0) { feito.current = false; return; }
    if (feito.current) return;
    feito.current = true;
    if (pontos.length === 1) {
      map.setView([pontos[0].lat, pontos[0].lng], 16);
    } else {
      map.fitBounds(pontos.map((p) => [p.lat, p.lng]), { padding: [40, 40], maxZoom: 17 });
    }
  }, [pontos, ativo, map]);
  return null;
}

// O container pode ganhar tamanho depois de criado (layout em duas colunas):
// sem isto o Leaflet desenha so uma parte dos tiles.
function AjustarTamanho() {
  const map = useMap();
  useEffect(() => {
    const t = setTimeout(() => map.invalidateSize(), 250);
    const aoRedimensionar = () => map.invalidateSize();
    window.addEventListener("resize", aoRedimensionar);
    return () => { clearTimeout(t); window.removeEventListener("resize", aoRedimensionar); };
  }, [map]);
  return null;
}

function Eventos({ ativo, area, onMover, onSair, onClicar, onDuploClique }) {
  useMapEvents({
    mousemove: (e) => { if (ativo) onMover({ lat: e.latlng.lat, lng: e.latlng.lng, x: e.containerPoint.x, y: e.containerPoint.y }); },
    mouseout: () => onSair(),
    click: (e) => {
      if (!ativo) return;
      // O segundo clique de um clique duplo nao e um ponto novo: o duplo clique
      // serve para finalizar a area.
      if (e.originalEvent?.detail >= 2) return;
      onClicar(e);
    },
    dblclick: () => { if (ativo && area) onDuploClique(); },
  });
  return null;
}

// Agrupamento de pins proximos (tela de listagem, quando `clusters`).
function Agrupamento({ pontos, markers, url, onClickMarker }) {
  const map = useMap();
  useEffect(() => {
    const grupo = L.markerClusterGroup({
      showCoverageOnHover: false,
      iconCreateFunction: (cluster) => L.divIcon({
        className: "",
        iconSize: [40, 40],
        html: `<div style="width:40px;height:40px;display:flex;align-items:center;justify-content:center;` +
          `background:url(${pin_cluster_icone}) center/contain no-repeat;color:green;font-size:20px;font-weight:700;">` +
          `${cluster.getChildCount()}</div>`,
      }),
    });
    pontos.forEach((p) => {
      const m = L.marker([p.lat, p.lng], { icon: icone(url), title: p.titulo || "" });
      if (onClickMarker) m.on("click", () => onClickMarker(markers[p._i]));
      grupo.addLayer(m);
    });
    map.addLayer(grupo);
    return () => { map.removeLayer(grupo); };
  }, [map, pontos, markers, url, onClickMarker]);
  return null;
}

function Mapa({
  markers,
  mapCenter,
  forceCenter,
  isEndArea = false,
  onClick,
  marker_icon,
  onClickMarker,
  onMarkerDragEnd,
  onMarkerRemove,
  onMarkerInsert,
  isPro,
  clusters = false,
  plantio = false,
  area = null,
}) {
  // Poligono de uma area selecionada (leitura). `area` = coordenadas do plantio.
  const areaLeitura = useMemo(() => {
    const pts = (area || []).map((c) => ({ lat: parseFloat(c.lat), lng: parseFloat(c.lng), p: c.posicao ?? 0 }))
      .filter((c) => Number.isFinite(c.lat) && Number.isFinite(c.lng)).sort((a, b) => a.p - b.p);
    return pts.length >= 3 ? pts.map((c) => [c.lat, c.lng]) : null;
  }, [area]);
  const [endArea, setEndArea] = useState(isEndArea);
  const [mousePosition, setMousePosition] = useState(null);

  useEffect(() => { setEndArea(isEndArea); }, [isEndArea]);

  // Guarda o indice original: os callbacks de arraste usam a posicao na lista
  // recebida, mesmo que algum item venha sem coordenada.
  const pontos = useMemo(
    () => (markers || [])
      .map((m, i) => ({ ...m, lat: parseFloat(m.lat), lng: parseFloat(m.lng), _i: i }))
      .filter(valido),
    [markers]
  );

  // Lista de pontos sem coordenada valida nao pode manter a area "fechada".
  useEffect(() => {
    if ((markers || []).length === 0 && !plantio) setEndArea(false);
  }, [markers, plantio]);

  // Edicao da area: se a pessoa remove pontas (ou limpa a area) e a area fica com
  // menos de 3 pontos, o desenho REABRE para ela poder marcar mais pontos -- uma area
  // "fechada" nao aceita clique. So vale depois de a area ja ter tido pontos: na
  // edicao os pontos chegam do servidor DEPOIS do primeiro desenho do mapa.
  const jaTevePontos = useRef(false);
  useEffect(() => {
    const n = (markers || []).length;
    if (n > 0) { jaTevePontos.current = true; return; }
    if (jaTevePontos.current) { jaTevePontos.current = false; setEndArea(false); }
  }, [markers]);
  useEffect(() => {
    if (isPro && jaTevePontos.current && pontos.length > 0 && pontos.length < 3) setEndArea(false);
  }, [isPro, pontos.length]);

  // Pin que segue o mouse so existe na tela de registro (sem marker_icon) e
  // enquanto a area nao foi fechada.
  const ativo = !endArea && !marker_icon;

  const caminho = useMemo(() => {
    const base = pontos.map((p) => ({ lat: p.lat, lng: p.lng }));
    if (!endArea && mousePosition) base.push({ lat: mousePosition.lat, lng: mousePosition.lng });
    return base;
  }, [pontos, endArea, mousePosition]);

  const textoCartao = isPro
    ? (pontos.length > 1 ? "Clique duas vezes para finalizar o polígono" : "Clique em qualquer local para começar a desenhar")
    : "Clique em qualquer local para adicionar uma coordenada";

  // Gestos da edicao de area (so Profissional, e so quando ha o que editar).
  const ajuda = isPro && !clusters && !marker_icon
    ? (endArea
      ? "Arraste um ponto para movê-lo · botão direito num ponto para removê-lo · clique no + entre dois pontos para acrescentar um"
      : "Arraste um ponto para movê-lo · botão direito num ponto para removê-lo · clique no mapa para adicionar · duplo clique para finalizar")
    : null;

  const alvoFixo = valido(comoPonto(forceCenter)) ? comoPonto(forceCenter) : null;
  const alvoBase = valido(comoPonto(mapCenter)) ? comoPonto(mapCenter) : CENTRO_PADRAO;
  // Na edicao, o plantio ja registrado manda no enquadramento.
  const enquadrandoPlantio = plantio && pontos.length > 0;
  const alvo = enquadrandoPlantio ? null : (alvoFixo || alvoBase);

  const editavel = isPro && !marker_icon;
  const corIcone = (i) => (isPro
    ? (i === markers.length - 1 && endArea ? pin_success_icone : pin_pro_icone)
    : (marker_icon || pin_icone));

  return (
    <div style={{ height: "100vh", width: "100%", position: "relative" }}>
      <MapContainer
        center={[alvoBase.lat, alvoBase.lng]}
        zoom={12}
        style={{ height: "100%", width: "100%" }}
        // Com a area em desenho, duplo clique finaliza o poligono em vez de dar zoom.
        doubleClickZoom={!(isPro && ativo)}
      >
        <TileLayer url={TILES} attribution={ATRIBUICAO} maxZoom={19} />
        <AjustarTamanho />
        <Recentralizar alvo={alvo} />
        <EnquadrarPlantio pontos={pontos} ativo={enquadrandoPlantio} />
        <Eventos
          ativo={ativo}
          area={!!isPro}
          onMover={setMousePosition}
          onSair={() => setMousePosition(null)}
          onClicar={(e) => onClick && onClick({ latLng: comoLatLng(e.latlng.lat, e.latlng.lng) })}
          onDuploClique={() => { if (pontos.length > 1) setEndArea(true); }}
        />

        {clusters ? (
          <Agrupamento pontos={pontos} markers={markers} url={marker_icon || pin_icone} onClickMarker={onClickMarker} />
        ) : (
          pontos.map((p) => (
            <Marker
              key={`marker-${p._i}`}
              position={[p.lat, p.lng]}
              icon={editavel ? iconeVertice(corIcone(p._i)) : icone(corIcone(p._i))}
              draggable={!!isPro}
              title={p.titulo}
              eventHandlers={{
                drag: (e) => {
                  if (onMarkerDragEnd) {
                    const { lat, lng } = e.target.getLatLng();
                    onMarkerDragEnd(p._i, comoLatLng(lat, lng));
                  }
                },
                // Botão direito numa ponta a remove (o menu do navegador não abre).
                contextmenu: (e) => {
                  if (!isPro || !onMarkerRemove) return;
                  e.originalEvent?.preventDefault();
                  e.originalEvent?.stopPropagation();
                  onMarkerRemove(p._i);
                },
                click: () => {
                  if (onClickMarker) onClickMarker(markers[p._i]);
                  // Clicar no ultimo pin fecha a area. So faz sentido para a
                  // area Profissional (antes o Individual tambem "fechava" e
                  // perdia a chance de reposicionar o ponto).
                  else if (isPro && p._i === markers.length - 1) setEndArea(true);
                },
              }}
            />
          ))
        )}

        {isPro && endArea && onMarkerInsert && !clusters && !marker_icon && pontos.length >= 3 &&
          pontos.map((p, i) => {
            const prox = pontos[(i + 1) % pontos.length];
            const lat = (p.lat + prox.lat) / 2;
            const lng = (p.lng + prox.lng) / 2;
            return (
              <Marker
                key={`meio-${p._i}`}
                position={[lat, lng]}
                icon={ICONE_MEIO}
                title="Acrescentar uma ponta aqui"
                zIndexOffset={-500}
                eventHandlers={{ click: () => onMarkerInsert(p._i, comoLatLng(lat, lng)) }}
              />
            );
          })}

        {ativo && mousePosition && (
          <Marker
            position={[mousePosition.lat, mousePosition.lng]}
            icon={icone(isPro ? pin_plus_icone : pin_icone)}
            interactive={false}
            keyboard={false}
            zIndexOffset={1000}
          />
        )}

        {isPro && caminho.length > 1 && (
          <Polygon
            positions={caminho.map((c) => [c.lat, c.lng])}
            pathOptions={{ fillColor: "#D7E3DA", fillOpacity: 0.8, color: "#3E8E5B", opacity: 1, weight: 3 }}
          />
        )}
        {areaLeitura && (
          <>
            <Polygon
              positions={areaLeitura}
              pathOptions={{ fillColor: "#2AA444", fillOpacity: 0.25, color: "#1f7a33", opacity: 1, weight: 3 }}
            />
            <EnquadrarArea area={areaLeitura} />
          </>
        )}
      </MapContainer>

      {ajuda && <div className="mapa-ajuda" role="note">{ajuda}</div>}

      {ativo && mousePosition && (pontos.length === 0 || pontos.length > 1) && (
        <div
          style={{
            position: "absolute",
            zIndex: 1100,
            pointerEvents: "none",
            transform: "translate(-50%, -100%)",
            left: `${mousePosition.x}px`,
            top: `${mousePosition.y - 30}px`,
            background: "white",
            padding: "8px",
            borderRadius: "8px",
            boxShadow: "0 2px 6px rgba(0, 0, 0, 0.3)",
            whiteSpace: "nowrap",
          }}
        >
          {textoCartao}
        </div>
      )}
    </div>
  );
}

export default Mapa;
